import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogOut, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rw_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (t) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { "x-admin-token": t },
      });
      setLeads(data);
    } catch (e) {
      setError(
        e?.response?.status === 401
          ? "Wrong token. Try again."
          : "Could not load leads."
      );
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const onLogin = (e) => {
    e.preventDefault();
    localStorage.setItem(TOKEN_KEY, input);
    setToken(input);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-ink px-4">
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="bg-brand-card border border-white/10 rounded-sm p-8 max-w-md w-full"
        >
          <h1 className="font-serif text-3xl text-white">Renderwall · Admin</h1>
          <p className="mt-2 text-white/60 text-sm">
            Enter your admin token to view incoming quote requests.
          </p>
          <div className="mt-6">
            <Label className="text-white/70 text-xs uppercase tracking-[0.18em]">
              Admin token
            </Label>
            <Input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="admin-token-input"
              className="mt-2 bg-brand-ink border-white/10 text-white rounded-sm h-12"
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-login-button"
            className="mt-6 bg-brand-gold hover:bg-brand-goldDark text-black font-semibold w-full rounded-sm py-5"
          >
            View leads
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-ink">
      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          <h1 className="font-serif text-2xl md:text-3xl text-white">
            Lead inbox
          </h1>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => load(token)}
              className="border-white/10 text-white hover:bg-white/5 rounded-sm"
              data-testid="admin-refresh-button"
            >
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={logout}
              className="border-white/10 text-white hover:bg-white/5 rounded-sm"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {loading && (
          <div className="text-white/70 flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-brand-gold" />
            Loading leads…
          </div>
        )}
        {error && <div className="text-red-400">{error}</div>}
        {!loading && leads.length === 0 && !error && (
          <div className="text-white/60">
            No leads yet — share your site to start collecting quote requests.
          </div>
        )}

        <div className="grid gap-4 md:gap-6">
          {leads.map((l) => (
            <div
              key={l.id}
              data-testid={`admin-lead-${l.id}`}
              className="bg-brand-card border border-white/5 rounded-sm p-6 md:p-8 grid md:grid-cols-12 gap-6"
            >
              <div className="md:col-span-4">
                <div className="text-xs uppercase tracking-[0.22em] text-brand-gold/90">
                  {new Date(l.created_at).toLocaleString("en-GB")}
                </div>
                <div className="mt-2 font-serif text-2xl text-white">
                  {l.name}
                </div>
                <a
                  href={`tel:${l.phone}`}
                  className="mt-3 inline-flex items-center gap-2 text-white/80 hover:text-brand-gold"
                >
                  <Phone className="h-4 w-4 text-brand-gold" /> {l.phone}
                </a>
                <a
                  href={`mailto:${l.email}`}
                  className="mt-1 flex items-center gap-2 text-white/80 hover:text-brand-gold break-all"
                >
                  <Mail className="h-4 w-4 text-brand-gold" /> {l.email}
                </a>
              </div>
              <div className="md:col-span-8 grid grid-cols-2 gap-4 text-sm">
                <Field label="Service" value={l.service} />
                <Field label="Project" value={l.project_type} />
                <Field label="Postcode" value={l.postcode || "—"} />
                <Field label="Status" value={l.status} />
                <div className="col-span-2">
                  <div className="text-xs uppercase tracking-[0.22em] text-white/50">
                    Message
                  </div>
                  <div className="mt-1 text-white/80 whitespace-pre-wrap">
                    {l.message || "—"}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.22em] text-white/50">
      {label}
    </div>
    <div className="mt-1 text-white">{value}</div>
  </div>
);
